import type * as Sentry from '@sentry/react';
import { isAxiosError } from 'axios';
import type { Primitive } from 'type-fest';

type ApiRequestMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * Use a prefix so that this is unlikely to collide with any other property.
 *
 * Use a string instead of a symbol so that request info will be an enumerable
 * property and can be included by Sentry's extra error data integration.
 */
export const apiRequestInfoKey = '__adflowReq';

export interface ApiRequestInfo {
	method: ApiRequestMethod;
	pathPattern: string;
	params: Record<string, unknown> | undefined;
	query: Record<string, unknown> | undefined;
}

export function addApiRequestInfo(
	error: unknown,
	apiRequestInfo: ApiRequestInfo
) {
	if (typeof error === 'object' && error !== null) {
		Object.assign(error, {
			[apiRequestInfoKey]: apiRequestInfo,
		});
	}
}

function getApiRequestInfo(error: unknown) {
	const errorObject =
		typeof error === 'object' && error !== null
			? (error as Record<string, unknown>)
			: undefined;
	const apiRequestInfo = errorObject?.[apiRequestInfoKey] as
		ApiRequestInfo | undefined;

	const axiosConfig = isAxiosError(error) ? error.config : undefined;

	return {
		method: (apiRequestInfo?.method ?? axiosConfig?.method)?.toUpperCase(),
		path: apiRequestInfo?.pathPattern ?? axiosConfig?.url,
		params: apiRequestInfo?.params,
		query: apiRequestInfo?.query ?? axiosConfig?.params,
	};
}

export function apiRequestInfoEventProcessor(
	event: Sentry.ErrorEvent,
	hint: Sentry.EventHint
) {
	const { method, path, params } = getApiRequestInfo(hint.originalException);

	if (method || path) {
		event.fingerprint = [
			...(event.fingerprint ?? ['{{ default }}']),
			path ?? '',
			method ?? '',
		];
	}

	event.tags = assignObjectToSentryTags(
		normalizeSentryTags(event.tags),
		{
			'': method && path ? `${method} ${path}` : undefined,
			method,
			path,
			params,
		},
		'apiReq'
	);

	return event;
}

function assignObjectToSentryTags(
	tags: Record<string, string>,
	obj: NonNullable<unknown>,
	prefix?: string
) {
	for (const [key, value] of Object.entries(obj)) {
		const tagKey = [prefix, key].filter(Boolean).join('.');
		if (typeof value === 'object' && value !== null) {
			assignObjectToSentryTags(tags, value, tagKey);
		} else {
			const tagValue = getSentryTagValue(value);
			if (tagValue !== undefined) {
				tags[tagKey] = tagValue;
			}
		}
	}

	return tags;
}

function normalizeSentryTags(
	tags: Record<string, Primitive> | undefined
): Record<string, string> {
	const normalizedTags: Record<string, string> = {};

	for (const [key, value] of Object.entries(tags ?? {})) {
		const tagValue = getSentryTagValue(value);
		if (tagValue !== undefined) {
			normalizedTags[key] = tagValue;
		}
	}

	return normalizedTags;
}

function getSentryTagValue(value: unknown): string | undefined {
	switch (typeof value) {
		case 'bigint':
		case 'boolean':
		case 'number':
		case 'string': {
			return String(value);
		}
		default: {
			return undefined;
		}
	}
}
